import { ascii, view, type BlockReader } from './reader';
import { parseComments } from './vorbis';
import type { MediaInfo } from './index';

/**
 * Chapters and cover art from an Ogg Vorbis or Opus file.
 *
 * The tags live in the stream's second packet (after the identification
 * header), which can span several Ogg pages when it carries cover art. Pages
 * are read in order until that packet is complete; the audio is never touched.
 */

const MAX_PAGES = 4096;
const MAX_PACKET_BYTES = 16 * 1024 * 1024;

export async function readOgg(r: BlockReader): Promise<MediaInfo> {
  const packets = await firstPackets(r, 2);
  const comments = packets[1];
  if (!comments) return { chapters: [] };

  let body: Uint8Array | null = null;
  if (ascii(comments, 0, 8) === 'OpusTags') body = comments.subarray(8);
  else if (comments[0] === 3 && ascii(comments, 1, 6) === 'vorbis') body = comments.subarray(7);
  if (!body) return { chapters: [] };

  const parsed = parseComments(body);
  return {
    chapters: parsed.chapters,
    ...(parsed.picture ? { cover: { mimeType: parsed.picture.mimeType, data: parsed.picture.data } } : {}),
  };
}

/** Every stream starts with its first page up front, so an audio stream
 *  that has not shown itself by now is not coming. */
const MAX_PAGES_BEFORE_AUDIO = 32;

interface Stream {
  pending: Uint8Array[];
  pendingBytes: number;
  packets: Uint8Array[];
}

/**
 * Reassembles the first `wanted` packets of the Vorbis or Opus stream. A file
 * can hold other streams too — an Ogg Skeleton index often comes first — so
 * each stream's first packet decides whether it is the one to follow.
 */
async function firstPackets(r: BlockReader, wanted: number): Promise<Uint8Array[]> {
  const streams = new Map<number, Stream>();
  let chosen: number | null = null;
  let at = 0;

  for (let page = 0; page < MAX_PAGES; page++) {
    if (chosen === null && page >= MAX_PAGES_BEFORE_AUDIO) break;

    const h = await r.bytes(at, 27);
    if (h.length < 27 || ascii(h, 0, 4) !== 'OggS') break;
    const serial = view(h).getUint32(14, true);
    const segmentCount = h[26] ?? 0;
    const lacing = await r.bytes(at + 27, segmentCount);
    const bodyLength = lacing.reduce((sum, n) => sum + n, 0);
    const bodyStart = at + 27 + segmentCount;
    at = bodyStart + bodyLength;

    let stream = streams.get(serial);
    if (!stream) streams.set(serial, (stream = { pending: [], pendingBytes: 0, packets: [] }));
    // Skip pages of streams already ruled out, without reading their bodies.
    if (chosen !== null ? serial !== chosen : stream.packets.length > 0) continue;

    const b = await r.bytes(bodyStart, bodyLength);
    let offset = 0;
    for (const n of lacing) {
      stream.pending.push(b.subarray(offset, offset + n));
      stream.pendingBytes += n;
      offset += n;
      if (stream.pendingBytes > MAX_PACKET_BYTES) return [];
      // A lacing value under 255 ends the packet.
      if (n < 255) {
        const packet = concat(stream.pending, stream.pendingBytes);
        stream.pending = [];
        stream.pendingBytes = 0;
        stream.packets.push(packet);
        if (stream.packets.length === 1 && isAudioHeader(packet)) chosen = serial;
        if (chosen === serial && stream.packets.length >= wanted) return stream.packets;
        if (chosen !== serial) break;
      }
    }
  }
  return chosen === null ? [] : (streams.get(chosen)?.packets ?? []);
}

function isAudioHeader(packet: Uint8Array): boolean {
  return ascii(packet, 0, 8) === 'OpusHead' || (packet[0] === 1 && ascii(packet, 1, 6) === 'vorbis');
}

function concat(parts: Uint8Array[], length: number): Uint8Array {
  const out = new Uint8Array(length);
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}
